import { useEffect } from 'react';
import { logErrorToServer } from '../appCall';
import StackTrace from 'stacktrace-js';

const extractUrlFromFetchArg = (arg: RequestInfo | URL): string => {
  if (typeof arg === 'string') {
    return arg;
  }

  if (arg instanceof Request) {
    return arg.url;
  }

  if (arg instanceof URL) {
    return arg.toString();
  }

  return 'unknown';
};

export const useGlobalErrorHandler = () => {
  useEffect(() => {
    // ✅ Runtime JS errors (like ReferenceError, TypeError, etc.)
    // 1. Listen to runtime JavaScript errors anywhere in the app
    const sanitizeFilename = (filename: string): string => {
      try {
        const url = new URL(filename);
        return url.pathname + url.search; // only path + query string
      } catch {
        return filename; // if not a URL, return as-is
      }
    };
    function sanitizeStack(stack: string): string {
      return stack
        .split('\n')
        .map((line) => {
          // Remove protocol + domain + port (e.g., http://localhost:3000 or https://mydomain.com:8080)
          line = line.replace(/https?:\/\/[^\/\s)]+/g, '');

          // Remove query parameters after filename, e.g., ?t=12345
          line = line.replace(/(\.tsx)(\?[^:\s)]+)/g, '$1');

          return line;
        })
        .join('\n');
    }
    const handleWindowError = async (event: ErrorEvent) => {
      try {
        const stackFrames = await StackTrace.fromError(event.error);
        const mappedStack = stackFrames
          .map((frame) => {
            // Use getter methods instead of direct properties
            const fileName =
              frame.getFileName()?.replace(/^https?:\/\/[^/]+/, '[REDACTED_DOMAIN]') || '[unknown]';
            const lineNumber = frame.getLineNumber();
            const columnNumber = frame.getColumnNumber();
            const functionName = frame.getFunctionName() || 'anonymous';

            return `${fileName}:${lineNumber}:${columnNumber} at ${functionName}`;
          })
          .join('\n');
        const sanitizedStackFromStackTrace = `"${mappedStack
          .replace(/https?:\/\/[^\s)]+/g, '')
          .replace(/\[REDACTED_DOMAIN\]/g, '')
          .trim()}"`;

        await logErrorToServer({
          type: 'runtime_error',
          message: event.message,
          file: sanitizeFilename(stackFrames[0]?.getFileName() || 'unknown'),
          line: stackFrames[0]?.getLineNumber(),
          column: stackFrames[0]?.getColumnNumber(),
          stackFrames: stackFrames.map((frame) => ({
            fileName: frame.getFileName(),
            lineNumber: frame.getLineNumber(),
            columnNumber: frame.getColumnNumber(),
            functionName: frame.getFunctionName()
          })),
          stack: sanitizedStackFromStackTrace
        });
      } catch (err) {
        await logErrorToServer({
          type: 'runtime_error',
          message: event.message,
          file: sanitizeFilename(event.filename),
          line: event.lineno,
          column: event.colno,
          stack: sanitizeStack(event.error?.stack)
        });
      }
    };

    // ✅ Unhandled Promise rejections (e.g. in async/await)
    // 2. Listen to unhandled Promise rejections (errors from async code without catch)
    const handleUnhandledRejection = async (event: PromiseRejectionEvent) => {
      await logErrorToServer({
        type: 'unhandled_promise',
        reason:
          typeof event.reason === 'object' && event.reason?.message
            ? event.reason.message
            : String(event.reason),
        stack:
          typeof event.reason === 'object' && event.reason?.stack ? event.reason.stack : 'No stack'
      });
    };

    // ✅ Intercept fetch to catch failed responses
    // 3. Override fetch() to catch failed network requests (HTTP errors & exceptions)

    const originalFetch = window.fetch;

    window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
      const url = extractUrlFromFetchArg(args[0]);
      if (url === import.meta.env.VITE_APP_POST_FRONTEND_ERROR_LOGGER_API) {
        return originalFetch(...args);
      }
      const init = args[1] || {};

      // Extract request body (if any)
      let requestBody: any = undefined;
      if (init.body) {
        try {
          requestBody = JSON.parse(init.body as string);
        } catch {
          requestBody = init.body;
        }
      }

      // Extract headers (normalize Headers object, array, or plain object)
      const requestHeaders: Record<string, string> = {};
      if (init.headers) {
        let headersEntries: [string, string][];
        if (init.headers instanceof Headers) {
          headersEntries = Array.from(init.headers.entries());
        } else if (Array.isArray(init.headers)) {
          headersEntries = init.headers;
        } else {
          headersEntries = Object.entries(init.headers);
        }

        headersEntries.forEach(([key, value]) => {
          if (key.toLowerCase() !== 'authorization') {
            requestHeaders[key] = value;
          }
        });
      }

      try {
        const response = await originalFetch(...args);

        if (!response.ok) {
          const responseMessage = await response.clone().text();
          // request body , URL , headers
          await logErrorToServer({
            type: 'fetch_error',
            url,
            status: response.status,
            statusText: response.statusText,
            requestBody,
            requestHeaders,
            message: responseMessage
          });
        }

        return response;
      } catch (error: any) {
        // Log fetch network failure (e.g., network down, server unreachable)
        await logErrorToServer({
          type: 'fetch_exception',
          url,
          error: error.message,
          status: error.status,
          requestBody,
          requestHeaders
        });
        throw error; // Re-throw to let caller handle as well
      }
    };
    // Add listeners
    // 4. Register the above listeners on window for errors and unhandled promise rejections
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Clean up listeners on unmount
    // 5. Cleanup on unmount: remove event listeners and restore original fetch()
    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.fetch = originalFetch;
    };
  }, []);
};
